import type {
  BalanceTableRow,
  JsonBalanceExtended,
  JsonBalanceGroup,
} from 'src/store/shared/balances/types';
import type {
  BalanceHistoryTableRow,
  JsonBalanceHistoryExtended,
  JsonBalanceHistoryGroup,
} from 'src/store/shared/balance-history/types';
import { hasProperty } from 'src/utils/type-guards';

/**
 * Whether a frame belongs to the `api-keys` section's old, flat envelope (the
 * sub-accounts page, pre-SKY-3540-v2 backend contract): `{ mainAccounts,
 * subAccounts, apiKeys }` under the same `Balances` command the Loans/Retainers
 * sections use. The sub-accounts page keeps sending its `Listen`, so a backend
 * that hasn't picked up Global.Clients#711 yet (or was rolled back) can still
 * answer with this shape — Loans/Retainers must not mistake it for their own.
 */
export const isSubAccountsSectionFrame = (json: unknown): boolean =>
  hasProperty(json, 'mainAccounts');

/**
 * Type guard to check if an item is a group
 */
export function isGroup(item: { key: string }): boolean {
  return item.key.includes('group');
}

/**
 * Type guard to check if an item is a JsonBalanceExtended (not a group)
 */
export function isJsonBalanceExtended(
  item:
    | JsonBalanceExtended
    | JsonBalanceGroup
    | JsonBalanceHistoryExtended
    | JsonBalanceHistoryGroup,
): item is JsonBalanceExtended {
  return (
    !hasProperty(item, 'children') &&
    hasProperty(item, 'assets') &&
    !('comparison' in (item.assets[0] ?? {}))
  );
}

/**
 * Type guard to check if an item is a JsonBalanceHistoryExtended (not a group)
 */
export function isJsonBalanceHistoryExtended(
  item:
    | JsonBalanceExtended
    | JsonBalanceGroup
    | JsonBalanceHistoryExtended
    | JsonBalanceHistoryGroup,
): item is JsonBalanceHistoryExtended {
  return !('children' in item) && 'accountID' in item && 'comparison' in (item.assets[0] ?? {});
}

/**
 * A history *account* row (including error / empty accounts, whose `assets`
 * array is empty). Unlike `isJsonBalanceHistoryExtended`, this does not require
 * a `comparison` field on the first asset, so it correctly includes error rows.
 */
export function isHistoryAccountRow(
  item: BalanceHistoryTableRow,
): item is JsonBalanceHistoryExtended {
  return (
    !('children' in item) &&
    'accountID' in item &&
    !String(item.key).startsWith('subtotal:') &&
    !String(item.key).startsWith('group:')
  );
}

/**
 * Type guard to check if an item is a subtotal row
 */
export function isSubtotal(item: { label?: string }): boolean {
  return item.label === 'Subtotal';
}

/**
 * Helper to safely convert balance data to table rows.
 * The data source can be a union of types that all satisfy BalanceTableRow requirements.
 */
export const isBalanceTableRows = (
  data: JsonBalanceExtended[] | readonly unknown[],
): data is readonly BalanceTableRow[] => {
  return data.every((item) => hasProperty(item, 'assets'));
};

/**
 * Helper to safely convert balance history data to table rows.
 * The data source can be a union of types that all satisfy BalanceHistoryTableRow requirements.
 */
export const isBalanceHistoryTableRows = (
  data: JsonBalanceHistoryExtended[] | readonly unknown[],
): data is readonly BalanceHistoryTableRow[] => {
  return data.every((item) => hasProperty(item, 'assets'));
};
